import { Skeleton, Box, Alert } from '@mui/material';
import { ApolloError } from '@apollo/client';

import { MetricPanel, MetricPanelProps } from '@occc/ui-design-system';
import { RetailerNetworkScanDataTobaccoSubmissionsOverviewQuery } from '@occc/graphql';

import { SUBMISSIONS_OVERVIEW_SKELETON_HEIGHT } from './retailer-network-tobacco-last-weeks-submission-overview.constants';

type RetailerNetworkTobaccoLastWeeksSubmissionOverviewProps = {
  loading?: boolean;
  error?: ApolloError;
  retailerNetworkScanDataTobaccoSubmissionsOverview?: RetailerNetworkScanDataTobaccoSubmissionsOverviewQuery['retailerNetworkScanDataTobaccoSubmissionsOverview'];
};

export const RetailerNetworkTobaccoLastWeeksSubmissionOverview = ({
  loading,
  error,
  retailerNetworkScanDataTobaccoSubmissionsOverview,
}: RetailerNetworkTobaccoLastWeeksSubmissionOverviewProps) => {
  if (loading) {
    return (
      <Skeleton variant='rectangular' width='100%' height={SUBMISSIONS_OVERVIEW_SKELETON_HEIGHT} />
    );
  }

  if (error || !retailerNetworkScanDataTobaccoSubmissionsOverview) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: SUBMISSIONS_OVERVIEW_SKELETON_HEIGHT,
        }}
      >
        <Alert severity='error'>
          Failed to load Last Week's Submission Overview. Please try again later.
        </Alert>
      </Box>
    );
  }

  const { totalSites, totalSitesSubmitted, estimatedReimbursements } =
    retailerNetworkScanDataTobaccoSubmissionsOverview;

  const metrics: MetricPanelProps['metrics'] = [
    {
      label: 'Multi Unit Reimbursements',
      value: estimatedReimbursements.multiUnit,
      valuePrefix: '$',
      isTrendHidden: true,
    },
    {
      label: 'Loyalty Reimbursements',
      value: estimatedReimbursements.loyalty,
      valuePrefix: '$',
      isTrendHidden: true,
    },
    {
      label: 'Stores Submitted',
      value: totalSites ? (totalSitesSubmitted * 100) / totalSites : 0,
      valueSuffix: '%',
      isTrendHidden: true,
      tooltip: `Only ${totalSitesSubmitted} of ${totalSites} stores submitted last week. Reconnect your locations to maximize reimbursement and avoid missed opportunities.`,
    },
  ];

  return (
    <MetricPanel
      title='Last Week’s Submission Overview'
      description='All reimbursement values are estimates'
      metrics={metrics}
    />
  );
};
